import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    MessageSquare,
    AlertCircle,
    CheckCircle2,
    Plus,
    Loader2,
    Lock,
    ShieldAlert,
    Home,
    FileText,
    DollarSign,
    Webhook
} from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { motion } from 'framer-motion';
import { SiteHeader } from '@/components/site-header';
import { EmptyState as EmptyStateComponent } from '@/components/ui/empty-state';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { containerVariants } from '@/components/animations';
import { useAuth } from '@/context/AuthContext';
import { 
    listTenants, 
    createTenant,
    listAgents, 
    getWhatsAppOnboarding,
    getThirdPartySettings,
    updateThirdPartySettings,
    listWhatsAppTemplates,
    createWhatsAppTemplate,
    getWhatsAppTemplateById,
    deleteWhatsAppTemplate
} from '@/api/tenants/tenants.api';
import type { 
    Tenant, 
    TenantCreateRequest, 
    Agent,
    ThirdPartySettings,
    ThirdPartySettingsUpdateRequest,
    WhatsAppTemplate,
    WhatsAppTemplateCreateRequest
} from '@/api/tenants/tenants.types';
import { EnableMessagingDialog } from './components/EnableMessagingDialog';
import { TenantSummaryCard } from './components/TenantSummaryCard';
import { ThirdPartySettingsCard } from './components/ThirdPartySettingsCard';
import { TemplateManagementCard } from './components/TemplateManagementCard';
import { TemplateDetailsDialog } from './components/TemplateDetailsDialog';
import { MessagingSkeleton } from './components/MessagingSkeleton';
import { MessagingAccountCard, MessagingAccountSkeleton } from './components/MessagingAccountCard';
import { WhatsAppOnboardingFlow } from './components/WhatsAppOnboardingFlow';
import { 
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface EmptyStateProps {
    onEnable: () => void;
    canEnable: boolean;
}

const EmptyState = ({ onEnable, canEnable }: EmptyStateProps) => {
    return (
        <EmptyStateComponent
            icon={MessageSquare}
            title="WhatsApp Messaging"
            description="Enable messaging services for your organization."
            secondaryDescription="Connect with customers through WhatsApp Business."
            action={{
                label: "Enable Messaging Service",
                onClick: onEnable,
                icon: Plus,
                disabled: !canEnable,
                disabledReason: "You need admin permissions to enable messaging services. Contact your administrator for access.",
            }}
            infoCard={{
                title: "Pricing Structure",
                content: (
                    <div className="space-y-3">
                        <div className="flex items-start gap-2">
                            <DollarSign className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                            <p className="text-xs text-muted-foreground">
                                <span className="font-medium">Setup fee</span>, <span className="font-medium">monthly base fee</span>, and <span className="font-medium">price per transaction</span>
                            </p>
                        </div>
                        <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                            <p className="text-xs text-muted-foreground">
                                Voice processing services (STT/TTS) are <span className="font-medium text-amber-600 dark:text-amber-400">billed separately</span>
                            </p>
                        </div>
                        <div className="pt-2 border-t">
                            <p className="text-xs text-muted-foreground">
                                For detailed pricing overview, please <span className="font-semibold text-primary">contact your representative</span> before activation.
                            </p>
                        </div>
                    </div>
                )
            }}
        />
    );
};

const Messaging = () => {
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingAgents, setIsLoadingAgents] = useState(false);
    const [isEnableDialogOpen, setIsEnableDialogOpen] = useState(false);
    const [isSettingUp, setIsSettingUp] = useState(false);
    const [isSettingUpWhatsApp, setIsSettingUpWhatsApp] = useState(false);
    const [showOnboardingFlow, setShowOnboardingFlow] = useState(false);
    
    // New state for configurations
    const [thirdPartySettings, setThirdPartySettings] = useState<ThirdPartySettings | null>(null);
    const [isLoadingSettings, setIsLoadingSettings] = useState(false);
    const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    
    // Template details dialog state
    const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
    const [isTemplateDetailsOpen, setIsTemplateDetailsOpen] = useState(false);
    const [isLoadingTemplateDetails, setIsLoadingTemplateDetails] = useState(false);

    const { error, success } = useEnhancedToast();
    const errorRef = useRef(error);
    const { user } = useAuth();

    const canManageMessaging = user?.permissions?.messaging === 'EDITOR';

    // Update ref when error function changes
    useEffect(() => {
        errorRef.current = error;
    }, [error]);

    const fetchAgents = useCallback(async () => {
        setIsLoadingAgents(true);
        try {
            const response = await listAgents();
            if (response.data) {
                console.log('Agents data received:', response.data);
                setAgents(response.data);
            } else {
                setAgents([]);
            }
        } catch (error) {
            console.error('Failed to fetch agents:', error);
            setAgents([]);
        } finally {
            setIsLoadingAgents(false);
        }
    }, []);

    // Fetch third-party settings
    const fetchThirdPartySettings = useCallback(async () => {
        setIsLoadingSettings(true);
        try {
            const response = await getThirdPartySettings();
            if (response.data) {
                setThirdPartySettings(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch third-party settings:', error);
            setThirdPartySettings(null);
        } finally {
            setIsLoadingSettings(false);
        }
    }, []);

    // Fetch WhatsApp templates
    const fetchTemplates = useCallback(async () => {
        setIsLoadingTemplates(true);
        try {
            const response = await listWhatsAppTemplates();
            if (response.data) {
                setTemplates(response.data);
            } else {
                setTemplates([]);
            }
        } catch (error) {
            console.error('Failed to fetch templates:', error);
            setTemplates([]);
        } finally {
            setIsLoadingTemplates(false);
        }
    }, []);

    const fetchTenant = useCallback(async (isPolling = false) => {
        
        if (!isPolling) setIsLoading(true);
        try {
            // List tenants for the organization - should return at most 1 tenant
            const response = await listTenants();
            if (response.data && response.data.length > 0) {
                const tenant = response.data[0];
                
                // Check if tenant is fully activated (host must not be null)
                if (tenant.host) {
                    setTenant(tenant);
                    // Fetch agents and configurations for this tenant
                    fetchAgents();
                    fetchThirdPartySettings();
                    fetchTemplates();
                    if (isPolling) {
                        setIsSettingUp(false);
                    }
                    return true;
                } else {
                    // Tenant exists but is not fully activated yet
                    setTenant(null);
                    setAgents([]);
                    return false;
                }
            } else {
                setTenant(null);
                setAgents([]);
                return false;
            }
        } catch {
            // Expected to fail if no tenant exists
            setTenant(null);
            setAgents([]);
            return false;
        } finally {
            if (!isPolling) setIsLoading(false);
        }
    }, [fetchAgents, fetchThirdPartySettings, fetchTemplates]);

    useEffect(() => {
        fetchTenant();
    }, [fetchTenant]);

    // Polling effect for tenant setup
    useEffect(() => {
        if (!isSettingUp) return;

        let attempts = 0;
        const maxAttempts = 24; // 2 minutes at 5-second intervals

        const pollInterval = setInterval(async () => {
            const found = await fetchTenant(true);
            if (found) {
                success({
                    icon: CheckCircle2,
                    title: "Messaging Service Ready",
                    description: "Your messaging service is now active and ready to use.",
                    duration: 4000,
                });
                return;
            }

            attempts++;
            if (attempts >= maxAttempts) {
                setIsSettingUp(false);
                errorRef.current({
                    icon: AlertCircle,
                    title: "Activation Taking Longer Than Expected",
                    description: "Your messaging service is still being activated. Please refresh the page in a few minutes.",
                    duration: 6000,
                });
            }
        }, 5000); // Poll every 5 seconds

        return () => clearInterval(pollInterval);
    }, [isSettingUp, fetchTenant, success]);

    const handleEnableMessaging = async (data: TenantCreateRequest) => {
        try {
            const response = await createTenant(data);
            if (response.error) {
                throw new Error(response.error);
            }

            success({
                icon: CheckCircle2,
                title: "Messaging Service Requested",
                description: "Setting up your messaging service. This may take a few moments...",
                duration: 4000,
            });

            setIsEnableDialogOpen(false);
            setIsSettingUp(true);
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Error",
                description: err instanceof Error ? err.message : "Failed to enable messaging service",
                duration: 4000,
            });
        }
    };

    // Keeping for potential future use
    // const handleUpdateTenant = async (id: string, meta: Record<string, unknown>) => {
    //     try {
    //         const response = await updateTenantById(id, { meta });
    //         if (response.error) {
    //             throw new Error(response.error);
    //         }

    //         success({
    //             icon: CheckCircle2,
    //             title: "Settings Updated",
    //             description: "Tenant settings have been updated successfully.",
    //             duration: 3000,
    //         });

    //         fetchTenant(); // Refresh data
    //     } catch (err) {
    //         errorRef.current({
    //             icon: AlertCircle,
    //             title: "Error",
    //             description: err instanceof Error ? err.message : "Failed to update settings",
    //             duration: 4000,
    //         });
    //     }
    // };

    const copyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            success({
                icon: CheckCircle2,
                title: "Copied",
                description: `${label} copied to clipboard`,
                duration: 2000,
            });
        } catch {
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            success({
                icon: CheckCircle2,
                title: "Copied",
                description: `${label} copied to clipboard`,
                duration: 2000,
            });
        }
    };

    const handleWhatsAppSetup = async () => {
        if (!tenant) return;
        
        // Show the custom onboarding flow first
        setShowOnboardingFlow(true);
    };

    const handleOnboardingFlowComplete = async (proceed: boolean) => {
        setShowOnboardingFlow(false);
        
        if (!proceed || !tenant) return;
        
        setIsSettingUpWhatsApp(true);
        
        try {
            // Get onboarding data from API
            const result = await getWhatsAppOnboarding(tenant.id);
            
            if (result.error) {
                throw new Error(result.error);
            }

            if (!result.data) {
                throw new Error('No onboarding data received');
            }

            const { data: onboardingData } = result.data;

            // Store session data for callback page
            const sessionData = {
                tenantId: tenant.id,
                tenantName: tenant.name,
                customCallbackUrl: onboardingData.custom_callback_url
            };
            sessionStorage.setItem('whatsapp-onboarding-session', JSON.stringify(sessionData));

            // Construct callback URL
            const callbackUrl = `${window.location.origin}/whatsapp-onboarding-callback`;
            const encodedCallbackUrl = encodeURIComponent(callbackUrl);
            
            // Construct 360dialog URL with parameters
            const onboardingUrl = `${onboardingData.custom_entry_point_url}?lang=de&redirect_url=${encodedCallbackUrl}`;

            // Set up message listener for popup communication
            const handlePopupMessage = (event: MessageEvent) => {
                // Verify origin for security
                if (event.origin !== window.location.origin) {
                    return;
                }

                if (event.data.type === 'WHATSAPP_ONBOARDING_SUCCESS') {
                    // Handle successful onboarding
                    console.log('WhatsApp Business setup completed:', event.data.data);
                    
                    setIsSettingUpWhatsApp(false);
                    window.removeEventListener('message', handlePopupMessage);
                    
                    success({
                        icon: CheckCircle2,
                        title: "WhatsApp Setup Complete",
                        description: `WhatsApp has been successfully configured for ${tenant.name}.`,
                        duration: 5000,
                    });

                    // Clean up session storage
                    sessionStorage.removeItem('whatsapp-onboarding-session');
                    
                    // Refresh agents after successful onboarding
                    setTimeout(() => {
                        fetchAgents();
                    }, 2000);
                    
                } else if (event.data.type === 'WHATSAPP_ONBOARDING_ERROR') {
                    // Handle onboarding error
                    console.error('WhatsApp Business setup error:', event.data.error);
                    
                    setIsSettingUpWhatsApp(false);
                    window.removeEventListener('message', handlePopupMessage);
                    
                    errorRef.current({
                        icon: AlertCircle,
                        title: "Setup Failed",
                        description: event.data.error,
                        duration: 5000,
                    });
                }
            };

            // Add message listener
            window.addEventListener('message', handlePopupMessage);

            // Open popup with specific specifications
            const popup = window.open(
                onboardingUrl,
                'whatsapp-onboarding',
                'toolbar=no,menubar=no,width=600,height=900,top=100,left=100'
            );

            if (!popup) {
                window.removeEventListener('message', handlePopupMessage);
                throw new Error('Popup was blocked. Please allow popups for this site and try again.');
            }

            // Monitor popup for closure and check for completion
            const checkClosed = setInterval(() => {
                if (popup.closed) {
                    clearInterval(checkClosed);
                    window.removeEventListener('message', handlePopupMessage);
                    setIsSettingUpWhatsApp(false);
                    
                    // When popup is manually closed, refresh agents to check for new connections
                    setTimeout(() => {
                        fetchAgents();
                    }, 1000);
                }
            }, 1000);

            // Set a timeout to reset the loading state (fallback)
            setTimeout(() => {
                if (!popup.closed) {
                    setIsSettingUpWhatsApp(false);
                    clearInterval(checkClosed);
                    window.removeEventListener('message', handlePopupMessage);
                }
            }, 600000); // 10 minutes timeout

        } catch (error) {
            console.error('WhatsApp setup error:', error);
            setIsSettingUpWhatsApp(false);
            
            errorRef.current({
                icon: AlertCircle,
                title: "Setup Failed",
                description: error instanceof Error ? error.message : 'Failed to start WhatsApp setup',
                duration: 5000,
            });
        }
    };


    // Handle third-party settings update
    const handleUpdateThirdPartySettings = async (settings: ThirdPartySettingsUpdateRequest) => {
        try {
            const response = await updateThirdPartySettings(settings);
            if (response.data) {
                setThirdPartySettings(response.data);
                success({
                    icon: CheckCircle2,
                    title: "Settings Updated",
                    description: "Third-party settings have been updated successfully.",
                    duration: 3000,
                });
            }
        } catch (err) {
            error({
                icon: AlertCircle,
                title: "Update Failed",
                description: err instanceof Error ? err.message : "Failed to update settings",
                duration: 4000,
            });
            throw err; // Re-throw for component to handle
        }
    };

    // Handle template creation
    const handleCreateTemplate = async (template: WhatsAppTemplateCreateRequest) => {
        try {
            const response = await createWhatsAppTemplate(template);
            if (response.error) {
                // API returned an error object instead of throwing
                error({
                    icon: AlertCircle,
                    title: "Submission Failed",
                    description: response.error,
                    duration: 4000,
                });
                throw new Error(response.error);
            }

            // Check the actual status returned by the API
            const templateStatus = response.data?.status;
            
            if (templateStatus === 'rejected') {
                error({
                    icon: AlertCircle,
                    title: "Template Rejected",
                    description: "Your template was rejected by WhatsApp. Please review the content and try again.",
                    duration: 5000,
                });
            } else if (templateStatus === 'submitted' || templateStatus === 'pending') {
                success({
                    icon: CheckCircle2,
                    title: "Template Submitted for Approval",
                    description: "Your template has been submitted and is pending WhatsApp's review.",
                    duration: 4000,
                });
            } else if (templateStatus === 'approved') {
                success({
                    icon: CheckCircle2,
                    title: "Template Approved",
                    description: "Your template has been approved and is ready to use!",
                    duration: 4000,
                });
            } else {
                // Fallback for any other status
                success({
                    icon: CheckCircle2,
                    title: "Template Created",
                    description: `Template created with status: ${templateStatus}`,
                    duration: 4000,
                });
            }
            
            // Refresh templates list
            fetchTemplates();
        } catch (err) {
            // Parse error message for specific WhatsApp API errors
            const errorMessage = err instanceof Error ? err.message : "Failed to submit template";
            
            // Check for specific WhatsApp/Meta API errors
            if (errorMessage.includes("Message template language is being deleted") || 
                errorMessage.includes("New English content can't be added while the existing English content is being deleted")) {
                error({
                    icon: AlertCircle,
                    title: "Template Language Temporarily Unavailable",
                    description: "WhatsApp is processing a language deletion. Please wait 1-2 minutes and try again, or create a template with a different name.",
                    duration: 6000,
                });
            } else if (errorMessage.includes("selected agent id is invalid")) {
                error({
                    icon: AlertCircle,
                    title: "Invalid WhatsApp Agent",
                    description: "The selected WhatsApp agent is no longer valid. Please refresh the page and try again.",
                    duration: 4000,
                });
            } else {
                // Generic error handling - truncate very long error messages
                const displayMessage = errorMessage.length > 200 ? 
                    "There was an error submitting your template. Please try again." : 
                    errorMessage;
                    
                error({
                    icon: AlertCircle,
                    title: "Submission Failed",
                    description: displayMessage,
                    duration: 4000,
                });
            }
            throw err; // Re-throw for component to handle
        }
    };

    // Handle template deletion
    const handleDeleteTemplate = async (templateId: number) => {
        try {
            const response = await deleteWhatsAppTemplate(templateId);
            if (response.error) {
                // API returned an error object instead of throwing
                error({
                    icon: AlertCircle,
                    title: "Deletion Failed",
                    description: response.error,
                    duration: 4000,
                });
                throw new Error(response.error);
            }
            success({
                icon: CheckCircle2,
                title: "Template Deleted",
                description: "WhatsApp template has been deleted successfully.",
                duration: 3000,
            });
            // Refresh templates list
            fetchTemplates();
        } catch (err) {
            // Only show error toast if we haven't already handled the error response above
            if (!(err instanceof Error && err.message.includes("error"))) {
                error({
                    icon: AlertCircle,
                    title: "Deletion Failed",
                    description: err instanceof Error ? err.message : "Failed to delete template",
                    duration: 4000,
                });
            }
            throw err; // Re-throw for component to handle
        }
    };

    // Handle view template details
    const handleViewTemplate = async (templateId: number) => {
        setIsLoadingTemplateDetails(true);
        setIsTemplateDetailsOpen(true);
        setSelectedTemplate(null);
        
        try {
            const response = await getWhatsAppTemplateById(templateId);
            if (response.data) {
                setSelectedTemplate(response.data);
            }
        } catch (err) {
            error({
                icon: AlertCircle,
                title: "Failed to Load",
                description: err instanceof Error ? err.message : "Failed to load template details",
                duration: 4000,
            });
            setIsTemplateDetailsOpen(false);
        } finally {
            setIsLoadingTemplateDetails(false);
        }
    };

    // Action button components following assistants pattern
    const AddWhatsAppButton = () => (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div>
                        <Button 
                            onClick={handleWhatsAppSetup} 
                            size="sm"
                            className="gap-2"
                            disabled={!canManageMessaging || isSettingUpWhatsApp}
                        >
                            {isSettingUpWhatsApp ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <WhatsAppIcon className="h-4 w-4" />
                                    Add WhatsApp
                                    {!canManageMessaging && <Lock className="h-3.5 w-3.5 opacity-70" />}
                                </>
                            )}
                        </Button>
                    </div>
                </TooltipTrigger>
                {!canManageMessaging && (
                    <TooltipContent className="flex items-center gap-2 max-w-xs">
                        <ShieldAlert className="h-4 w-4 text-amber-500" />
                        <p>You need editor permissions to manage messaging accounts. Contact your administrator for access.</p>
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );


    return (
        <div className="flex flex-col h-full">
            <SiteHeader
                title="Messaging Services"
                subtitle="Manage WhatsApp messaging integrations"
            >
                {/* Action Buttons - Only show when tenant exists */}
                {!isLoading && tenant && (
                    <AddWhatsAppButton />
                )}
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                {isLoading ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <MessagingSkeleton />
                    </motion.div>
                ) : isSettingUp ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-8">
                            <div className="text-center space-y-6">
                                <div className="flex justify-center">
                                    <div className="relative">
                                        <div className="h-12 w-12 rounded-xl bg-muted/50 flex items-center justify-center ring-1 ring-border">
                                            <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-3">
                                    <h3 className="text-lg font-semibold">Activating Messaging Service</h3>
                                    <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
                                        Your messaging service is being activated. This usually takes a few moments.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : !tenant ? (
                    <div className="h-full flex items-center justify-center">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="w-full -mt-12"
                        >
                            <EmptyState 
                                onEnable={() => setIsEnableDialogOpen(true)} 
                                canEnable={canManageMessaging} 
                            />
                        </motion.div>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        {/* Tabbed Interface */}
                        <Tabs defaultValue="overview" className="space-y-4">
                                <TabsList className="h-11">
                                    <TabsTrigger value="overview" className="gap-2 px-4">
                                        <Home className="h-4 w-4" />
                                        Overview
                                    </TabsTrigger>
                                    <TabsTrigger value="accounts" className="gap-2 px-4">
                                        <MessageSquare className="h-4 w-4" />
                                        Accounts
                                    </TabsTrigger>
                                    <TabsTrigger value="settings" className="gap-2 px-4">
                                        <Webhook className="h-4 w-4" />
                                        Webhook
                                    </TabsTrigger>
                                    <TabsTrigger value="templates" className="gap-2 px-4">
                                        <FileText className="h-4 w-4" />
                                        Templates
                                    </TabsTrigger>
                                </TabsList>

                                {/* Overview Tab */}
                                <TabsContent value="overview" className="m-0 space-y-6">
                                    {/* Tenant Configuration Summary */}
                                    <TenantSummaryCard tenant={tenant} />
                                </TabsContent>

                                {/* Accounts Tab */}
                                <TabsContent value="accounts" className="m-0 space-y-6">
                                    {/* Messaging Accounts Grid or Empty State */}
                                    {isLoadingAgents ? (
                                        <motion.div
                                            variants={containerVariants}
                                            initial="hidden"
                                            animate="visible"
                                            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                                        >
                                            <MessagingAccountSkeleton />
                                            <MessagingAccountSkeleton />
                                            <MessagingAccountSkeleton />
                                        </motion.div>
                                    ) : agents.length === 0 ? (
                                        <motion.div
                                            variants={containerVariants}
                                            initial="hidden"
                                            animate="visible"
                                        >
                                            <Card className="shadow-sm border-dashed">
                                                <div className="p-12 flex flex-col items-center justify-center text-center space-y-4">
                                                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                                                        <MessageSquare className="h-8 w-8 text-muted-foreground" />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <h3 className="text-base font-semibold">No Messaging Accounts</h3>
                                                        <p className="text-sm text-muted-foreground max-w-sm">
                                                            Connect WhatsApp accounts to start messaging with your customers.
                                                        </p>
                                                    </div>
                                                    <AddWhatsAppButton />
                                                </div>
                                            </Card>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            variants={containerVariants}
                                            initial="hidden"
                                            animate="visible"
                                            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                                        >
                                            {/* WhatsApp Accounts */}
                                            {agents
                                                .filter(agent => agent.channel === 'whatsapp')
                                                .map((agent) => (
                                                    <MessagingAccountCard
                                                        key={agent.display_id}
                                                        agent={agent}
                                                        type="whatsapp"
                                                        onCopyDetails={copyToClipboard}
                                                    />
                                                ))
                                            }

                                            {/* Facebook Accounts */}
                                            {agents
                                                .filter(agent => agent.channel === 'facebook')
                                                .map((agent) => (
                                                    <MessagingAccountCard
                                                        key={agent.display_id}
                                                        agent={agent}
                                                        type="facebook"
                                                        onCopyDetails={copyToClipboard}
                                                    />
                                                ))
                                            }
                                        </motion.div>
                                    )}
                                </TabsContent>

                                {/* Settings Tab */}
                                <TabsContent value="settings" className="m-0 space-y-6">
                                    <ThirdPartySettingsCard
                                        settings={thirdPartySettings || undefined}
                                        isLoading={isLoadingSettings}
                                        onUpdateSettings={handleUpdateThirdPartySettings}
                                    />
                                </TabsContent>

                                {/* Templates Tab */}
                                <TabsContent value="templates" className="m-0 space-y-6">
                                    <TemplateManagementCard
                                        templates={templates}
                                        agents={agents}
                                        isLoading={isLoadingTemplates}
                                        onCreateTemplate={handleCreateTemplate}
                                        onDeleteTemplate={handleDeleteTemplate}
                                        onViewTemplate={handleViewTemplate}
                                        onRefreshTemplates={fetchTemplates}
                                    />
                                </TabsContent>
                            </Tabs>
                    </motion.div>
                )}
            </div>

            <EnableMessagingDialog
                open={isEnableDialogOpen}
                onOpenChange={setIsEnableDialogOpen}
                onEnable={handleEnableMessaging}
            />

            <WhatsAppOnboardingFlow
                isOpen={showOnboardingFlow}
                onClose={() => setShowOnboardingFlow(false)}
                onComplete={handleOnboardingFlowComplete}
            />

            <TemplateDetailsDialog
                isOpen={isTemplateDetailsOpen}
                onClose={() => setIsTemplateDetailsOpen(false)}
                template={selectedTemplate}
                isLoading={isLoadingTemplateDetails}
            />
        </div>
    );
};

export default Messaging; 